import React, { useState, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import { ResourceLoading } from "../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../components/utils/ResourceError";
import {
  ColleagueDialog,
  resolveColleagueContact,
} from "../../../components/utils/ColleagueDialog";
import {
  BookOpenIcon,
  UserGroupIcon,
  ShieldCheckIcon,
  DocumentTextIcon,
  ChevronRightIcon,
  PhoneIcon,
  EnvelopeIcon,
  BuildingOfficeIcon,
  ArrowLeftIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  XCircleIcon,
  CheckCircleIcon,
  MagnifyingGlassIcon,
  WrenchScrewdriverIcon,
  ClipboardDocumentListIcon,
  PlusIcon,
  BeakerIcon,
} from "@heroicons/react/24/outline";
import {
  getHseDocuments,
  getHseDocument,
  acknowledgeDocumentVersionRead,
} from "../../../api/hse/documents";
import { getDisciplines } from "../../../api/hse/disciplines";
import { toast } from "react-hot-toast";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { HtmlTextFormatter } from "../../../components/Utils";
import { fetchUsers } from "../../../api/user";
import { fetchHseEvents } from "../../../api/hse/events";
import { fetchOrgCharts } from "../../../api/quality/orgCharts";
import { getChemicalInventorySettings } from "../../../api/hse/chemicalInventorySettings";
import { HandbookChemicalInventoryTab } from "../../../components/dashboard/quality/handbook-chemical-inventory";
import { useUserHasAccess } from "../../../routes/roleOnly";
import {
  CHEMICAL_INVENTORY_WRITE_ROLES,
  normalizeChemicalInventorySettings,
} from "../../../utils/chemicalInventoryAccess";
import { createDocumentForm } from "../../../api/documentForms";
import { EmployeeToolsDialog } from "../../../components/dashboard/tools/EmployeeToolsDialog";
import { DocumentFileContent } from "../hse-ks/documents/DocumentFileContent";
import NewDocumentFormDialog from "../../../components/dashboard/document-forms/NewDocumentFormDialog";
import "../../../components/utils/PromangRichTextEditor.css";
import { ReadRequirementNotice } from "../../../components/dashboard/hse/compliance/ReadRequirementNotice";
import { HandbookDocumentLibrary, HandbookReadStatus } from "../../../components/dashboard/quality/handbook/HandbookDocumentLibrary";
import { isMustRead } from "../../../components/dashboard/quality/handbook/handbookLibrary";
import { notifyHseReadChanged } from "../../../api/hse/documentCompliance";

const isFillableFormDocument = (doc) => {
  const templateId = resolveFillableTemplateId(doc);
  return Boolean(templateId && doc?.fillable_template?.active !== false);
};

function resolveFillableTemplateId(doc) {
  const tpl = doc?.fillable_template;
  if (!tpl) return null;
  return tpl.id ?? tpl.template_id ?? null;
}

function documentLocationLabel(doc) {
  if (!doc) return null;
  const parts = [];
  if (doc.discipline?.name) parts.push(doc.discipline.name);
  if (doc.category?.name || doc.category_display) {
    parts.push(doc.category?.name || doc.category_display);
  }
  if (doc.chapter?.number) {
    const chapterLabel = [doc.chapter.number, doc.chapter.title].filter(Boolean).join(" ");
    parts.push(chapterLabel);
  }
  return parts.length > 0 ? parts.join(" · ") : null;
}

function compareChapterNumbers(a, b) {
  const aParts = String(a || "")
    .split(".")
    .map((part) => parseInt(part, 10) || 0);
  const bParts = String(b || "")
    .split(".")
    .map((part) => parseInt(part, 10) || 0);
  const length = Math.max(aParts.length, bParts.length);

  for (let i = 0; i < length; i += 1) {
    const diff = (aParts[i] || 0) - (bParts[i] || 0);
    if (diff !== 0) return diff;
  }

  return 0;
}

function flattenChapterTreeForHandbook(chapters = [], parentChapterId = null) {
  return (chapters || []).flatMap((chapter) => {
    const normalized = {
      ...chapter,
      parent_chapter_id: chapter.parent_chapter_id ?? parentChapterId,
      children: [],
    };
    const nested = chapter.sub_chapters || chapter.subChapters || chapter.children || [];
    return [normalized, ...flattenChapterTreeForHandbook(nested, normalized.id)];
  });
}

function seedDocumentStructureFromDisciplines(structure, disciplines = []) {
  disciplines.forEach((discipline) => {
    if (!discipline?.id) return;

    if (!structure.disciplines[discipline.id]) {
      structure.disciplines[discipline.id] = {
        discipline,
        categories: {},
      };
    } else {
      structure.disciplines[discipline.id].discipline = {
        ...structure.disciplines[discipline.id].discipline,
        ...discipline,
      };
    }

    (discipline.categories || []).forEach((category) => {
      if (!category?.id) return;

      const disciplineData = structure.disciplines[discipline.id];
      if (!disciplineData.categories[category.id]) {
        disciplineData.categories[category.id] = {
          category,
          chapters: {},
          withoutChapter: [],
        };
      } else {
        disciplineData.categories[category.id].category = {
          ...disciplineData.categories[category.id].category,
          ...category,
        };
      }

      const categoryData = disciplineData.categories[category.id];
      flattenChapterTreeForHandbook(category.chapters || category.root_chapters || []).forEach((chapter) => {
        if (!chapter?.id || categoryData.chapters[chapter.id]) return;
        categoryData.chapters[chapter.id] = {
          chapter,
          documents: [],
        };
      });
    });
  });
}

export const EmployeeHandbookView = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.session.currentUser);
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState("overview");
  const [documents, setDocuments] = useState({}); // Legacy structure by chapter
  const [documentsByStructure, setDocumentsByStructure] = useState({}); // New structure: discipline -> category -> chapter
  const [documentsWithoutChapter, setDocumentsWithoutChapter] = useState([]);
  const [allDocuments, setAllDocuments] = useState([]); // Flat list for search
  const [chapters, setChapters] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [hseEvents, setHseEvents] = useState([]);
  const [orgCharts, setOrgCharts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formsSearchQuery, setFormsSearchQuery] = useState("");
  const [colleagueDialogOpen, setColleagueDialogOpen] = useState(false);
  const [selectedColleague, setSelectedColleague] = useState(null);
  const [orgChartDialogOpen, setOrgChartDialogOpen] = useState(false);
  const [chemicalSettings, setChemicalSettings] = useState(null);
  const canWriteChemical = useUserHasAccess({
    roles: CHEMICAL_INVENTORY_WRITE_ROLES,
  });
  const [toolsDialogOpen, setToolsDialogOpen] = useState(false);
  const [documentDialogOpen, setDocumentDialogOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [documentContent, setDocumentContent] = useState(null);
  const [loadingDocument, setLoadingDocument] = useState(false);
  const [ackSubmitting, setAckSubmitting] = useState(false);
  const [chapterSiblingsExpanded, setChapterSiblingsExpanded] = useState(true);
  const [showFillFormDialog, setShowFillFormDialog] = useState(false);
  const [fillFormTemplateId, setFillFormTemplateId] = useState(null);
  const [fillFormDefaultTitle, setFillFormDefaultTitle] = useState("");
  const [creatingFormTemplateId, setCreatingFormTemplateId] = useState(null);
  const documentContentScrollRef = useRef(null);

  useEffect(() => {
    loadData();
  }, []);


  // Handle URL-based tab navigation
  useEffect(() => {
    const tabFromUrl = searchParams.get("tab");
    if (!tabFromUrl) return;
    const validTabs = [
      "overview",
      "documents",
      "form_templates",
      "employees",
      "hse_history",
      "chemical-inventory",
    ];
    if (validTabs.includes(tabFromUrl)) {
      setActiveTab(tabFromUrl);
      return;
    }
    const tabIndex = parseInt(tabFromUrl, 10);
    if (!Number.isNaN(tabIndex) && tabIndex >= 0 && tabIndex < validTabs.length) {
      setActiveTab(validTabs[tabIndex]);
    }
  }, [searchParams]);

  // Deep link from the HMS reminder, front page and e-mails: ?tab=documents&document=ID
  const deepLinkDocumentId = searchParams.get("document");
  useEffect(() => {
    if (!deepLinkDocumentId || isLoading) return;
    const doc = allDocuments.find((item) => String(item.id) === String(deepLinkDocumentId));
    setActiveTab("documents");
    if (doc) handleDocumentClick(doc);
    const next = new URLSearchParams(searchParams);
    next.delete("document");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkDocumentId, isLoading, allDocuments]);

  const loadData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Approved controlled documents + registered fillable form gateways (skjemamal).
      const [documentsRes, disciplinesRes, employeesRes, hseEventsRes, orgChartsRes, chemSettingsRes] =
        await Promise.all([
          getHseDocuments({ status: "approved", include_fillable_forms: true }).catch(() => ({
            documents: [],
          })),
          getDisciplines().catch(() => ({ company_disciplines: [] })),
          fetchUsers({
            filters: [{ name: "EMPLOYEE", value: true }],
            active: true, // Only active employees
            per_page: 500,
            sort: "asc",
            sortBy: "name",
            // Work contact only — never next-of-kin (PII) on a company-wide handbook list
            fields:
              "id,displayTitle,name,email,mobile,mobile_work,department,employment.id,employment.email,employment.mobile,employment.phone,employment.employee_number,working_posistion,active,employee_number",
          }).catch(() => ({ data: [] })),
          fetchHseEvents({ per_page: 10 }).catch(() => ({ data: [] })),
          fetchOrgCharts({ per_page: 50 }).catch(() => ({ data: [] })),
          getChemicalInventorySettings().catch(() => null),
        ]);

      const allDocuments = documentsRes?.documents || [];

      // Organize documents by structure: Discipline -> Category -> Chapter -> Documents
      const structure = {
        disciplines: {}, // discipline_id -> { discipline, categories: { category_id -> { category, chapters: {...} } } }
        withoutDiscipline: {
          categories: {}, // category_id -> { category, chapters: {...} }
          withoutCategory: {
            chapters: {}, // chapter_id -> { chapter, documents: [] }
            withoutChapter: []
          }
        }
      };
      seedDocumentStructureFromDisciplines(
        structure,
        disciplinesRes?.company_disciplines || []
      );

      // Extract unique chapters and build chapter map with hierarchy
      const chapterMap = new Map();
      const documentsByChapter = {}; // Legacy structure
      const documentsWithoutChapterList = [];

      allDocuments.forEach(doc => {
        // Build chapter map with parent relationships
        if (doc.chapter) {
          const chapterId = doc.chapter.id;
          if (!chapterMap.has(chapterId)) {
            chapterMap.set(chapterId, {
              ...doc.chapter,
              parent_chapter_id: doc.chapter.parent_chapter_id || null,
              children: []
            });
          }
          // Legacy structure
          if (!documentsByChapter[chapterId]) {
            documentsByChapter[chapterId] = {
              chapter: doc.chapter,
              documents: []
            };
          }
          documentsByChapter[chapterId].documents.push(doc);
        } else {
          documentsWithoutChapterList.push(doc);
        }

        // Build hierarchical structure
        const disciplineId = doc.discipline?.id || 'no_discipline';
        const categoryId = doc.category?.id || 'no_category';
        const chapterId = doc.chapter?.id || 'no_chapter';

        if (disciplineId !== 'no_discipline') {
          // Document has discipline
          if (!structure.disciplines[disciplineId]) {
            structure.disciplines[disciplineId] = {
              discipline: doc.discipline,
              categories: {}
            };
          }
          
          if (categoryId !== 'no_category') {
            // Document has category
            if (!structure.disciplines[disciplineId].categories[categoryId]) {
              structure.disciplines[disciplineId].categories[categoryId] = {
                category: doc.category,
                chapters: {}
              };
            }
            
            if (chapterId !== 'no_chapter') {
              // Document has chapter
              if (!structure.disciplines[disciplineId].categories[categoryId].chapters[chapterId]) {
                structure.disciplines[disciplineId].categories[categoryId].chapters[chapterId] = {
                  chapter: doc.chapter,
                  documents: []
                };
              }
              structure.disciplines[disciplineId].categories[categoryId].chapters[chapterId].documents.push(doc);
            } else {
              // Document has discipline and category but no chapter
              if (!structure.disciplines[disciplineId].categories[categoryId].withoutChapter) {
                structure.disciplines[disciplineId].categories[categoryId].withoutChapter = [];
              }
              structure.disciplines[disciplineId].categories[categoryId].withoutChapter.push(doc);
            }
          } else {
            // Document has discipline but no category
            if (!structure.disciplines[disciplineId].withoutCategory) {
              structure.disciplines[disciplineId].withoutCategory = {
                chapters: {},
                withoutChapter: []
              };
            }
            if (chapterId !== 'no_chapter') {
              if (!structure.disciplines[disciplineId].withoutCategory.chapters[chapterId]) {
                structure.disciplines[disciplineId].withoutCategory.chapters[chapterId] = {
                  chapter: doc.chapter,
                  documents: []
                };
              }
              structure.disciplines[disciplineId].withoutCategory.chapters[chapterId].documents.push(doc);
            } else {
              structure.disciplines[disciplineId].withoutCategory.withoutChapter.push(doc);
            }
          }
        } else {
          // Document has no discipline
          if (categoryId !== 'no_category') {
            // Document has category but no discipline
            if (!structure.withoutDiscipline.categories[categoryId]) {
              structure.withoutDiscipline.categories[categoryId] = {
                category: doc.category,
                chapters: {}
              };
            }
            
            if (chapterId !== 'no_chapter') {
              if (!structure.withoutDiscipline.categories[categoryId].chapters[chapterId]) {
                structure.withoutDiscipline.categories[categoryId].chapters[chapterId] = {
                  chapter: doc.chapter,
                  documents: []
                };
              }
              structure.withoutDiscipline.categories[categoryId].chapters[chapterId].documents.push(doc);
            } else {
              if (!structure.withoutDiscipline.categories[categoryId].withoutChapter) {
                structure.withoutDiscipline.categories[categoryId].withoutChapter = [];
              }
              structure.withoutDiscipline.categories[categoryId].withoutChapter.push(doc);
            }
          } else {
            // Document has no discipline and no category
            if (chapterId !== 'no_chapter') {
              if (!structure.withoutDiscipline.withoutCategory.chapters[chapterId]) {
                structure.withoutDiscipline.withoutCategory.chapters[chapterId] = {
                  chapter: doc.chapter,
                  documents: []
                };
              }
              structure.withoutDiscipline.withoutCategory.chapters[chapterId].documents.push(doc);
            } else {
              structure.withoutDiscipline.withoutCategory.withoutChapter.push(doc);
            }
          }
        }
      });

      // Build chapter hierarchy from chapterMap
      const chaptersArray = Array.from(chapterMap.values());
      chaptersArray.forEach(chapter => {
        if (chapter.parent_chapter_id) {
          const parent = chapterMap.get(chapter.parent_chapter_id);
          if (parent) {
            if (!parent.children) parent.children = [];
            parent.children.push(chapter);
          }
        }
      });

      // Sort chapters
      const sortChapters = (chapters) => {
        return chapters.sort((a, b) => {
          return compareChapterNumbers(a.number, b.number);
        }).map(ch => ({
          ...ch,
          children: ch.children ? sortChapters(ch.children) : []
        }));
      };

      const rootChapters = chaptersArray.filter(ch => !ch.parent_chapter_id);
      const sortedChapters = sortChapters(rootChapters);

      setChapters(sortedChapters);
      setDocuments(documentsByChapter); // Keep for legacy compatibility
      setDocumentsByStructure(structure); // New hierarchical structure
      setDocumentsWithoutChapter(documentsWithoutChapterList);
      // Store flat list for search
      setAllDocuments(allDocuments);

      // Handle employees data - filter to only active employees with employment
      const employeesData = employeesRes?.data || [];
      const activeEmployees = Array.isArray(employeesData) 
        ? employeesData.filter(emp => emp.active && emp.employment) 
        : [];
      setEmployees(activeEmployees);

      // Handle HSE events data
      const hseEventsData = hseEventsRes?.data || [];
      setHseEvents(Array.isArray(hseEventsData) ? hseEventsData : []);

      // Handle org charts data - only show approved ones. 403 / missing quality role → empty.
      const orgChartsForbidden =
        orgChartsRes?.errors ||
        orgChartsRes?.error ||
        orgChartsRes?.status === 403 ||
        orgChartsRes?.status === 401;
      const orgChartsData = orgChartsForbidden ? [] : orgChartsRes?.data || [];
      const approvedCharts = Array.isArray(orgChartsData) 
        ? orgChartsData.filter(chart => chart.approved_revision) 
        : [];
      setOrgCharts(approvedCharts);
      setChemicalSettings(
        chemSettingsRes ? normalizeChemicalInventorySettings(chemSettingsRes) : null
      );
    } catch (err) {
      console.error("Error loading employee handbook data:", err);
      setError("Failed to load data");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDocumentClick = async (document) => {
    setSelectedDocument(document);
    setDocumentContent(null);
    setDocumentDialogOpen(true);
    setLoadingDocument(true);
    setChapterSiblingsExpanded(true);
    if (documentContentScrollRef.current) {
      documentContentScrollRef.current.scrollTop = 0;
    }
    
    try {
      // Fetch full document content
      const response = await getHseDocument(document.id);
      setDocumentContent(response?.document);
    } catch (err) {
      console.error("Error loading document:", err);
      setError("Failed to load document content");
    } finally {
      setLoadingDocument(false);
    }
  };

  const handleAcknowledgeRead = async () => {
    if (!selectedDocument?.id || !documentContent?.approved_version_id) return;
    setAckSubmitting(true);
    try {
      await acknowledgeDocumentVersionRead(
        selectedDocument.id,
        documentContent.approved_version_id,
        null
      );
      toast.success(t("hse.document.acknowledge_read_success"));
      notifyHseReadChanged();
      await loadData();
      setDocumentContent((prev) =>
        prev
          ? {
              ...prev,
              current_user_read_status: {
                ...(prev.current_user_read_status || {}),
                exempt_as_creator: prev.current_user_read_status?.exempt_as_creator,
                acknowledged_read_understood: true,
                read_understood_at: new Date().toISOString(),
              },
            }
          : prev
      );
    } catch (err) {
      const msg =
        err?.response?.data?.errors?.[0] || t("hse.document.acknowledge_read_error");
      toast.error(msg);
    } finally {
      setAckSubmitting(false);
    }
  };

  const handleEmployeeClick = (employee) => {
    setSelectedColleague(employee);
    setColleagueDialogOpen(true);
  };

  const getHseEventTypeIcon = (eventType) => {
    switch (eventType) {
      case "injury":
        return <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />;
      case "near_miss":
        return <ClockIcon className="h-5 w-5 text-yellow-500" />;
      case "property_damage":
        return <XCircleIcon className="h-5 w-5 text-orange-500" />;
      case "environmental":
        return <ExclamationTriangleIcon className="h-5 w-5 text-green-500" />;
      default:
        return <ExclamationTriangleIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getHseEventStatusBadge = (status) => {
    const baseClasses =
      "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
    switch (status) {
      case "draft":
        return (
          <span className={`${baseClasses} bg-gray-100 text-gray-800`}>
            {t("Draft")}
          </span>
        );
      case "submitted":
        return (
          <span className={`${baseClasses} bg-blue-100 text-blue-800`}>
            {t("Submitted")}
          </span>
        );
      case "under_review":
        return (
          <span className={`${baseClasses} bg-yellow-100 text-yellow-800`}>
            {t("Under Review")}
          </span>
        );
      case "closed":
        return (
          <span className={`${baseClasses} bg-green-100 text-green-800`}>
            {t("Closed")}
          </span>
        );
      default:
        return (
          <span className={`${baseClasses} bg-gray-100 text-gray-800`}>
            {status}
          </span>
        );
    }
  };

  // Function to organize chapters in hierarchical structure
  const organizeChaptersHierarchy = (chapters) => {
    if (!chapters || !Array.isArray(chapters)) return [];
    
    const chapterMap = new Map();
    const rootChapters = [];
    
    // First pass: create map and identify root chapters
    chapters.forEach(chapter => {
      chapterMap.set(chapter.id, { ...chapter, children: chapter.children || [] });
      if (!chapter.parent_chapter_id) {
        rootChapters.push(chapterMap.get(chapter.id));
      }
    });
    
    // Second pass: build hierarchy if parent_chapter_id is available
    chapters.forEach(chapter => {
      if (chapter.parent_chapter_id) {
        const parent = chapterMap.get(chapter.parent_chapter_id);
        if (parent) {
          parent.children.push(chapterMap.get(chapter.id));
        } else {
          // Parent not found, treat as root chapter
          if (!rootChapters.find(c => c.id === chapter.id)) {
            rootChapters.push(chapterMap.get(chapter.id));
          }
        }
      }
    });
    
    // If no hierarchy found, return all chapters sorted by number
    if (rootChapters.length === 0) {
      return chapters.sort((a, b) => {
        return compareChapterNumbers(a.number, b.number);
      });
    }
    
    // Sort root chapters by number
    rootChapters.sort((a, b) => {
      return compareChapterNumbers(a.number, b.number);
    });
    
    return rootChapters;
  };

  const handbookChapterCount = useMemo(() => {
    const ids = new Set();
    const collectChapters = (chapters = {}) => {
      Object.values(chapters || {}).forEach((chapterData) => {
        if (chapterData.chapter?.id != null) ids.add(chapterData.chapter.id);
      });
    };

    Object.values(documentsByStructure.disciplines || {}).forEach((discipline) => {
      Object.values(discipline.categories || {}).forEach((category) => {
        collectChapters(category.chapters);
      });
      collectChapters(discipline.withoutCategory?.chapters);
    });
    Object.values(documentsByStructure.withoutDiscipline?.categories || {}).forEach((category) => {
      collectChapters(category.chapters);
    });
    collectChapters(documentsByStructure.withoutDiscipline?.withoutCategory?.chapters);

    allDocuments.forEach((d) => {
      if (d.chapter?.id != null) ids.add(d.chapter.id);
    });
    return ids.size;
  }, [allDocuments, documentsByStructure]);

  const formTemplatesCatalog = useMemo(() => {
    const seen = new Set();
    return allDocuments
      .filter(isFillableFormDocument)
      .filter((doc) => {
        const templateId = resolveFillableTemplateId(doc);
        if (!templateId || seen.has(templateId)) return false;
        seen.add(templateId);
        return true;
      })
      .sort((a, b) =>
        String(a.title || a.fillable_template?.title || "").localeCompare(
          String(b.title || b.fillable_template?.title || ""),
          "nb",
          { sensitivity: "base" }
        )
      );
  }, [allDocuments]);

  const filteredFormTemplatesCatalog = useMemo(() => {
    const query = formsSearchQuery.trim().toLowerCase();
    if (!query) return formTemplatesCatalog;

    return formTemplatesCatalog.filter((doc) => {
      const templateTitle = doc.fillable_template?.title || "";
      return (
        doc.title?.toLowerCase().includes(query) ||
        doc.description?.toLowerCase().includes(query) ||
        templateTitle.toLowerCase().includes(query) ||
        documentLocationLabel(doc)?.toLowerCase().includes(query)
      );
    });
  }, [formTemplatesCatalog, formsSearchQuery]);

  const openFillFormDialog = (templateId, defaultTitle = "") => {
    if (!templateId) {
      toast.error(t("documentForms.choose_template"));
      return;
    }
    setFillFormTemplateId(templateId);
    setFillFormDefaultTitle(defaultTitle || "");
    setShowFillFormDialog(true);
  };

  const createFormFromTemplate = async (doc, { closeDocumentDialog = false } = {}) => {
    const templateId = resolveFillableTemplateId(doc);
    if (!templateId) {
      toast.error(t("documentForms.choose_template"));
      return;
    }

    setCreatingFormTemplateId(templateId);
    try {
      const res = await createDocumentForm({
        document_form_template_id: Number(templateId),
        title: doc?.fillable_template?.title || doc?.title || undefined,
      });

      if (res?.errors) {
        toast.error(Array.isArray(res.errors) ? res.errors.join(", ") : t("documentForms.save_failed"));
        return;
      }

      const formId = res?.id ?? res?.data?.id;
      if (!formId) {
        toast.error(t("documentForms.save_failed"));
        return;
      }

      toast.success(
        res?.display_reference
          ? t("documentForms.created_with_reference", { reference: res.display_reference })
          : t("documentForms.created")
      );

      if (closeDocumentDialog) {
        setDocumentDialogOpen(false);
      }
      navigate(`/dashboard/hse-ks/documents/form-archive/${formId}`);
    } finally {
      setCreatingFormTemplateId(null);
    }
  };

  const showChemicalTab =
    chemicalSettings?.employee_visibility === "all_active" || canWriteChemical;

  const tabs = [
    { id: "overview", label: t("Overview"), mobileLabel: t("Overview"), icon: BookOpenIcon },
    { id: "documents", label: t("KS/HMS Documents"), mobileLabel: t("Documents"), icon: DocumentTextIcon },
    {
      id: "form_templates",
      label: t("employee_handbook.forms_tab"),
      mobileLabel: t("employee_handbook.forms_tab_mobile"),
      icon: ClipboardDocumentListIcon,
    },
    { id: "employees", label: t("Colleagues"), mobileLabel: t("Colleagues"), icon: UserGroupIcon },
    {
      id: "hse_history",
      label: t("HSE Historikk"),
      mobileLabel: "HSE",
      icon: ExclamationTriangleIcon,
    },
    ...(showChemicalTab
      ? [
          {
            id: "chemical-inventory",
            label: t("chemical.inventory"),
            mobileLabel: t("chemical.inventory"),
            icon: BeakerIcon,
          },
        ]
      : []),
  ];

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    const legacyTabs = [
      "overview",
      "documents",
      "form_templates",
      "employees",
      "hse_history",
    ];
    const legacyIndex = legacyTabs.indexOf(tabId);
    if (tabId === "chemical-inventory") {
      setSearchParams({ tab: "chemical-inventory" });
    } else if (legacyIndex !== -1) {
      setSearchParams({ tab: legacyIndex.toString() });
    }
  };

  const pendingAckCount = useMemo(() => allDocuments.filter(isMustRead).length, [allDocuments]);

  const chapterSiblingDocuments = useMemo(() => {
    const chapterId =
      selectedDocument?.chapter?.id ?? documentContent?.chapter?.id ?? null;
    if (!chapterId) return [];

    return allDocuments
      .filter(
        (doc) => doc.chapter?.id === chapterId && doc.id !== selectedDocument?.id
      )
      .sort((a, b) => {
        const na = String(a.document_number || a.title || "").toLowerCase();
        const nb = String(b.document_number || b.title || "").toLowerCase();
        return na.localeCompare(nb, "nb", { numeric: true });
      });
  }, [
    allDocuments,
    selectedDocument?.id,
    selectedDocument?.chapter?.id,
    documentContent?.chapter?.id,
  ]);

  const DocReadStatusBadge = ({ doc }) => <HandbookReadStatus doc={doc} />;

  const renderChapterSiblingDocuments = () => {
    if (chapterSiblingDocuments.length === 0) return null;

    const chapter = selectedDocument?.chapter || documentContent?.chapter;

    return (
      <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50/80">
        <button
          type="button"
          onClick={() => setChapterSiblingsExpanded((expanded) => !expanded)}
          className="flex w-full items-start justify-between gap-2 p-3 text-left sm:p-4"
          aria-expanded={chapterSiblingsExpanded}
        >
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-900">
              {t("employee_handbook.other_chapter_documents")}
            </p>
            {chapter ? (
              <p className="mt-0.5 text-xs text-slate-600">
                {chapter.number ? `#${chapter.number} · ` : ""}
                {chapter.title}
              </p>
            ) : null}
            <p className="mt-1 text-xs text-slate-500">
              {t("employee_handbook.other_chapter_documents_hint")}
            </p>
          </div>
          {chapterSiblingsExpanded ? (
            <ChevronUpIcon className="h-5 w-5 shrink-0 text-slate-400" />
          ) : (
            <ChevronDownIcon className="h-5 w-5 shrink-0 text-slate-400" />
          )}
        </button>
        {chapterSiblingsExpanded ? (
          <ul className="divide-y divide-slate-200 border-t border-slate-200">
            {chapterSiblingDocuments.map((doc) => (
              <li key={doc.id}>
                <button
                  type="button"
                  onClick={() => handleDocumentClick(doc)}
                  className="flex w-full items-start gap-3 p-3 text-left transition-colors hover:bg-slate-100 sm:px-4"
                >
                  <DocumentTextIcon className="h-4 w-4 shrink-0 text-blue-600" />
                  <div className="min-w-0 flex-1">
                    <p className="break-words text-sm font-medium text-slate-900">
                      {doc.title}
                    </p>
                    {doc.document_type_display ? (
                      <p className="mt-0.5 text-xs text-slate-500">
                        {doc.document_type_display}
                      </p>
                    ) : null}
                    {doc.document_number ? (
                      <p className="mt-0.5 font-mono text-[11px] text-slate-400">
                        {doc.document_number}
                      </p>
                    ) : null}
                  </div>
                  <DocReadStatusBadge doc={doc} />
                  <ChevronRightIcon className="h-4 w-4 shrink-0 text-slate-400" />
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    );
  };

  const fillableTemplateForDialog =
    documentContent?.fillable_template || selectedDocument?.fillable_template;
  const canFillFormFromHandbook = isFillableFormDocument({
    ...selectedDocument,
    fillable_template: fillableTemplateForDialog,
  });

  const renderOverview = () => (
    <div className="space-y-8">
      {/* Friendly hero greeting */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-blue-600 via-indigo-600 to-violet-600 p-5 text-white shadow-sm sm:p-7">
        <div className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-white/10 blur-2xl" />
        <div className="pointer-events-none absolute -bottom-12 -left-6 h-44 w-44 rounded-full bg-white/10 blur-2xl" />
        <div className="relative flex items-start gap-4">
          <span className="hidden h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white/15 ring-1 ring-white/25 backdrop-blur sm:inline-flex">
            <BookOpenIcon className="h-7 w-7" />
          </span>
          <div className="min-w-0">
            <h2 className="text-xl font-bold tracking-tight sm:text-2xl">
              {currentUser?.firstName
                ? `${t("Hei")}, ${currentUser.firstName} 👋`
                : t("Employee Handbook")}
            </h2>
            <p className="mt-1 max-w-xl text-sm text-white/85">
              {t("employee_handbook.forms_tab_subtitle")}
            </p>
          </div>
        </div>
      </div>

      {pendingAckCount > 0 ? (
        <div className="flex gap-3 rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 p-4 shadow-sm sm:p-5">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-600">
            <ExclamationTriangleIcon className="h-6 w-6" />
          </span>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-amber-950">
              {t("employee_handbook.pending_read_ack_title")}
            </p>
            <p className="mt-1 text-sm text-amber-900">
              {t("employee_handbook.pending_read_ack_body").replace(
                "{count}",
                String(pendingAckCount)
              )}
            </p>
            <button
              type="button"
              onClick={() => handleTabChange("documents")}
              className="mt-3 inline-flex items-center gap-1 rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-amber-700"
            >
              {t("employee_handbook.go_to_documents_tab")}
              <ChevronRightIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      ) : allDocuments.length > 0 ? (
        <div className="flex items-center gap-3 rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50 p-4 shadow-sm sm:p-5">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-100 text-emerald-600">
            <CheckCircleIcon className="h-6 w-6" />
          </span>
          <p className="text-sm font-medium text-emerald-950">
            {t("employee_handbook.all_documents_acknowledged")}
          </p>
        </div>
      ) : null}
      {/* Quick Stats */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <div className="rounded-2xl bg-gradient-to-br from-blue-50 to-blue-100/50 p-4 ring-1 ring-blue-100">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-blue-600/10 text-blue-600">
            <DocumentTextIcon className="h-5 w-5" />
          </span>
          <p className="mt-3 text-2xl font-bold tracking-tight text-blue-950 sm:text-3xl">
            {allDocuments.length}
          </p>
          <span className="mt-0.5 block text-xs font-medium text-blue-900/70">
            {t("KS/HMS Documents")}
          </span>
          {pendingAckCount > 0 ? (
            <p className="mt-1 text-[11px] font-semibold text-amber-700">
              {t("employee_handbook.stat_line_pending").replace(
                "{count}",
                String(pendingAckCount)
              )}
            </p>
          ) : null}
        </div>

        <div className="rounded-2xl bg-gradient-to-br from-emerald-50 to-green-100/50 p-4 ring-1 ring-emerald-100">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-600/10 text-emerald-600">
            <BookOpenIcon className="h-5 w-5" />
          </span>
          <p className="mt-3 text-2xl font-bold tracking-tight text-emerald-950 sm:text-3xl">
            {handbookChapterCount}
          </p>
          <span className="mt-0.5 block text-xs font-medium text-emerald-900/70">
            {t("Kapitler")}
          </span>
        </div>

        {formTemplatesCatalog.length > 0 ? (
          <button
            type="button"
            onClick={() => handleTabChange("form_templates")}
            className="group rounded-2xl bg-gradient-to-br from-indigo-50 to-indigo-100/50 p-4 text-left ring-1 ring-indigo-100 transition-all hover:-translate-y-0.5 hover:shadow-md"
          >
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-indigo-600/10 text-indigo-600">
              <ClipboardDocumentListIcon className="h-5 w-5" />
            </span>
            <p className="mt-3 text-2xl font-bold tracking-tight text-indigo-950 sm:text-3xl">
              {formTemplatesCatalog.length}
            </p>
            <span className="mt-0.5 block text-xs font-medium text-indigo-900/70">
              {t("employee_handbook.forms_tab")}
            </span>
          </button>
        ) : null}

        <div className="rounded-2xl bg-gradient-to-br from-rose-50 to-red-100/50 p-4 ring-1 ring-rose-100">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-rose-600/10 text-rose-600">
            <ExclamationTriangleIcon className="h-5 w-5" />
          </span>
          <p className="mt-3 text-2xl font-bold tracking-tight text-rose-950 sm:text-3xl">
            {hseEvents.length}
          </p>
          <span className="mt-0.5 block text-xs font-medium text-rose-900/70">
            {t("Hendelser")}
          </span>
        </div>

        <button
          type="button"
          onClick={() => setOrgChartDialogOpen(true)}
          className="group rounded-2xl bg-gradient-to-br from-purple-50 to-fuchsia-100/50 p-4 text-left ring-1 ring-purple-100 transition-all hover:-translate-y-0.5 hover:shadow-md"
        >
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-purple-600/10 text-purple-600">
            <UserGroupIcon className="h-5 w-5" />
          </span>
          <span className="mt-3 block text-sm font-semibold text-purple-950">
            {t("Organisasjonskart")}
          </span>
          <span className="mt-0.5 block text-xs font-medium text-purple-900/70">
            {orgCharts.length === 0
              ? t("Ingen godkjente kart")
              : `${orgCharts.length} ${orgCharts.length === 1 ? t("kart") : t("kart")}`}
          </span>
        </button>

        {showChemicalTab ? (
          <button
            type="button"
            onClick={() => handleTabChange("chemical-inventory")}
            className="group rounded-2xl bg-gradient-to-br from-cyan-50 to-teal-100/50 p-4 text-left ring-1 ring-cyan-100 transition-all hover:-translate-y-0.5 hover:shadow-md"
          >
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-cyan-600/10 text-cyan-700">
              <BeakerIcon className="h-5 w-5" />
            </span>
            <span className="mt-3 block text-sm font-semibold text-cyan-950">
              {t("chemical.inventory")}
            </span>
            <span className="mt-0.5 block text-xs font-medium text-cyan-900/70">
              {t("chemical.inventory.handbook_card")}
            </span>
          </button>
        ) : null}

        <button
          type="button"
          onClick={() => setToolsDialogOpen(true)}
          className="group rounded-2xl bg-gradient-to-br from-slate-50 to-slate-100/70 p-4 text-left ring-1 ring-slate-200 transition-all hover:-translate-y-0.5 hover:shadow-md"
        >
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-slate-600/10 text-slate-600">
            <WrenchScrewdriverIcon className="h-5 w-5" />
          </span>
          <span className="mt-3 block text-sm font-semibold text-slate-900">
            {t("tools")}
          </span>
          <span className="mt-0.5 block text-xs font-medium text-slate-500">
            {t("tool.employee_tools_short")}
          </span>
        </button>
      </div>

      {/* Recent Documents */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-blue-100 text-blue-600">
            <DocumentTextIcon className="h-4 w-4" />
          </span>
          <h3 className="text-base font-semibold text-gray-900 sm:text-lg">
            {t("Recent KS/HMS Documents")}
          </h3>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {(() => {
            const recentDocuments = [...allDocuments]
              .sort((a, b) => {
                const tb = new Date(b.updated_at || b.updatedAt || 0).getTime();
                const ta = new Date(a.updated_at || a.updatedAt || 0).getTime();
                return tb - ta;
              })
              .slice(0, 6);

            return recentDocuments.map((document) => (
              <div
                key={document.id}
                onClick={() => handleDocumentClick(document)}
                className="group cursor-pointer rounded-2xl border border-gray-200 bg-white p-4 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-blue-300 hover:shadow-md"
              >
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                  <div className="flex min-w-0 flex-1 items-start gap-3">
                    <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600 transition-colors group-hover:bg-blue-100">
                      <DocumentTextIcon className="h-5 w-5" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <h4 className="break-words font-medium text-gray-900">
                        {document.title}
                      </h4>
                      <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                        <p className="text-sm text-gray-500">
                          {document.chapter?.title || t("Uten kapittel")}
                        </p>
                        {document.document_type_display && (
                          <span className="text-xs text-gray-400">
                            • {document.document_type_display}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2 self-start sm:self-auto">
                    <DocReadStatusBadge doc={document} />
                    <ChevronRightIcon className="h-4 w-4 text-gray-400 transition-transform group-hover:translate-x-0.5" />
                  </div>
                </div>
              </div>
            ));
          })()}
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-rose-100 text-rose-600">
            <ExclamationTriangleIcon className="h-4 w-4" />
          </span>
          <h3 className="text-base font-semibold text-gray-900 sm:text-lg">
            {t("Nylige hendelser")}
          </h3>
        </div>
        <div className="space-y-3">
          {hseEvents.slice(0, 3).map((event) => (
            <Link
              key={event.id}
              to={`/dashboard/hse/events/${event.id}`}
              className="group block rounded-2xl border border-gray-200 bg-white p-4 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-blue-300 hover:shadow-md"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex min-w-0 flex-1 items-start gap-3">
                  <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gray-50">
                    {getHseEventTypeIcon(event.event_type)}
                  </span>
                  <div className="min-w-0 flex-1">
                    <h4 className="break-words font-medium text-gray-900">{event.title}</h4>
                    <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span className="text-xs text-gray-500">
                        {new Date(event.event_date).toLocaleDateString()}
                      </span>
                      {getHseEventStatusBadge(event.status)}
                    </div>
                  </div>
                </div>
                <ChevronRightIcon className="h-4 w-4 text-gray-400 transition-transform group-hover:translate-x-0.5" />
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );

  const renderDocuments = () => (
    <HandbookDocumentLibrary
      documents={allDocuments}
      onOpen={handleDocumentClick}
      initialStatus={searchParams.get("filter") || "all"}
    />
  );

  const renderFormTemplates = () => (
    <div className="space-y-6">
      <div className="flex items-start gap-3">
        <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
          <ClipboardDocumentListIcon className="h-5 w-5" />
        </span>
        <div>
          <h3 className="text-base font-semibold text-gray-900 sm:text-lg">
            {t("employee_handbook.forms_tab")}
          </h3>
          <p className="mt-0.5 text-sm text-gray-600">
            {t("employee_handbook.forms_tab_subtitle")}
          </p>
        </div>
      </div>

      <div className="relative">
        <MagnifyingGlassIcon className="pointer-events-none absolute left-3.5 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={formsSearchQuery}
          onChange={(e) => setFormsSearchQuery(e.target.value)}
          placeholder={t("employee_handbook.forms_tab_search")}
          className="w-full rounded-xl border border-gray-200 bg-white py-2.5 pl-11 pr-4 text-sm shadow-sm transition-shadow focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/30"
        />
      </div>

      {filteredFormTemplatesCatalog.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50 px-6 py-12 text-center">
          <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-white text-gray-400 shadow-sm">
            <ClipboardDocumentListIcon className="h-6 w-6" />
          </span>
          <p className="mt-3 text-sm font-medium text-gray-900">
            {formsSearchQuery.trim()
              ? t("employee_handbook.forms_tab_no_results")
              : t("employee_handbook.forms_tab_empty")}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {filteredFormTemplatesCatalog.map((doc) => {
            const templateId = resolveFillableTemplateId(doc);
            const location = documentLocationLabel(doc);
            const isCreating = creatingFormTemplateId === templateId;
            return (
              <article
                key={templateId || doc.id}
                className="flex flex-col rounded-2xl border border-indigo-100 bg-white p-5 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-indigo-200 hover:shadow-md"
              >
                <div className="mb-3 flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <span className="inline-flex items-center rounded-full bg-indigo-100 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-indigo-800">
                      {t("documentForms.hse.fillable_badge")}
                    </span>
                    <h4 className="mt-2 break-words text-base font-semibold text-gray-900">
                      {doc.fillable_template?.title || doc.title}
                    </h4>
                    {doc.description ? (
                      <p className="mt-1 line-clamp-2 text-sm text-gray-600">{doc.description}</p>
                    ) : null}
                    {location ? (
                      <p className="mt-2 text-xs text-gray-500">{location}</p>
                    ) : null}
                  </div>
                  <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-500">
                    <ClipboardDocumentListIcon className="h-5 w-5" />
                  </span>
                </div>

                <div className="mt-auto flex flex-col gap-2 pt-2 sm:flex-row">
                  <button
                    type="button"
                    disabled={isCreating}
                    onClick={() => createFormFromTemplate(doc)}
                    className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-indigo-700 disabled:opacity-50"
                  >
                    <PlusIcon className="h-4 w-4" />
                    {isCreating ? t("Loading") : t("documentForms.hse.fill_form")}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDocumentClick(doc)}
                    className="inline-flex flex-1 items-center justify-center rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                  >
                    {t("employee_handbook.forms_tab_info")}
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );

  const renderEmployees = () => (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {employees.map((employee) => {
        const initials = String(employee.name || "")
          .split(" ")
          .filter(Boolean)
          .slice(0, 2)
          .map((part) => part[0]?.toUpperCase())
          .join("");
        const contact = resolveColleagueContact(employee);
        const phone = contact.phone;
        const email = contact.email;
        return (
          <div
            key={employee.id}
            onClick={() => handleEmployeeClick(employee)}
            className="group cursor-pointer rounded-2xl border border-gray-200 bg-white p-4 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-blue-300 hover:shadow-md"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex min-w-0 flex-1 items-start gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 text-sm font-semibold text-white shadow-sm">
                  {initials || <UserGroupIcon className="h-5 w-5" />}
                </div>
                <div className="min-w-0 flex-1">
                  <h4 className="break-words font-semibold text-gray-900">{employee.name}</h4>
                  <div className="mt-1.5 flex flex-col gap-1">
                    {employee.department && (
                      <div className="flex items-center gap-1.5">
                        <BuildingOfficeIcon className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                        <span className="truncate text-xs text-gray-500">
                          {employee.department.displayTitle}
                        </span>
                      </div>
                    )}
                    {phone && (
                      <div className="flex items-center gap-1.5">
                        <PhoneIcon className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                        <span className="break-all text-xs text-gray-500">
                          {phone}
                        </span>
                      </div>
                    )}
                    {email && (
                      <div className="flex items-center gap-1.5">
                        <EnvelopeIcon className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                        <span className="break-all text-xs text-gray-500">
                          {email}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <ChevronRightIcon className="h-4 w-4 shrink-0 text-gray-400 transition-transform group-hover:translate-x-0.5" />
            </div>
          </div>
        );
      })}
    </div>
  );

  const renderHseHistory = () => (
    <div className="space-y-3">
      {hseEvents.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50 px-6 py-12 text-center">
          <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-white text-gray-400 shadow-sm">
            <ExclamationTriangleIcon className="h-6 w-6" />
          </span>
          <h3 className="mt-3 text-base font-semibold text-gray-900">
            {t("Ingen hendelser")}
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {t("Ingen hendelser er rapportert ennå.")}
          </p>
        </div>
      ) : (
        hseEvents.map((event) => (
          <Link
            key={event.id}
            to={`/dashboard/hse/events/${event.id}`}
            className="group block rounded-2xl border border-gray-200 bg-white p-4 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-blue-300 hover:shadow-md"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex min-w-0 flex-1 items-start gap-3">
                <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gray-50">
                  {getHseEventTypeIcon(event.event_type)}
                </span>
                <div className="min-w-0 flex-1">
                  <h4 className="break-words font-semibold text-gray-900">{event.title}</h4>
                  <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1">
                    <span className="text-xs text-gray-500">
                      {new Date(event.event_date).toLocaleDateString()}
                    </span>
                    {getHseEventStatusBadge(event.status)}
                    {event.location && (
                      <div className="flex items-center space-x-1">
                        <BuildingOfficeIcon className="h-3 w-3 text-gray-400" />
                        <span className="text-xs text-gray-500">
                          {event.location}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <ChevronRightIcon className="h-4 w-4 shrink-0 text-gray-400 transition-transform group-hover:translate-x-0.5" />
            </div>
          </Link>
        ))
      )}
    </div>
  );

  const renderContent = () => {
    if (isLoading) {
      return <ResourceLoading />;
    }

    if (error) {
      return <ResourceError errors={[error]} />;
    }

    switch (activeTab) {
      case "overview":
        return renderOverview();
      case "documents":
        return renderDocuments();
      case "form_templates":
        return renderFormTemplates();
      case "employees":
        return renderEmployees();
      case "hse_history":
        return renderHseHistory();
      case "chemical-inventory":
        return showChemicalTab ? <HandbookChemicalInventoryTab /> : renderOverview();
      default:
        return renderOverview();
    }
  };

  return (
    <>
      <DashboardHeader
        compact
        subtitle=""
        title={t("Employee Handbook")}
        meta={[{ label: t("Employee Handbook"), icon: BookOpenIcon }]}
        mainButton={{
          label: t("Back"),
          action: () => navigate(-1),
          path: null,
          color: "",
          icon: ArrowLeftIcon,
        }}
      >
        <div className="mx-auto flex w-full max-w-5xl flex-1 min-h-0 flex-col overflow-hidden px-0 sm:mt-8 sm:px-6 lg:px-8">
          {/* Tabs stay fixed above scrollable content in compact full-screen layout */}
          <div className="z-20 flex-shrink-0 border-b border-gray-200 bg-white/95 p-3 shadow-sm backdrop-blur sm:mb-6 sm:border-b-0 sm:bg-transparent sm:p-0 sm:shadow-none">
            <div className="grid grid-cols-3 gap-1.5 rounded-2xl bg-gray-100/80 p-1.5 ring-1 ring-gray-200/60 sm:flex sm:gap-1 sm:overflow-x-auto">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`relative flex min-h-[3rem] flex-col items-center justify-center gap-1 rounded-xl px-2 py-2 text-xs font-medium transition-all duration-200 sm:min-h-0 sm:flex-1 sm:flex-row sm:gap-2 sm:px-3 sm:py-2.5 sm:text-sm ${
                    activeTab === tab.id
                      ? "bg-white text-blue-600 shadow-sm ring-1 ring-blue-100"
                      : "text-gray-500 hover:bg-white/60 hover:text-gray-900"
                  }`}
                >
                  <tab.icon className="h-5 w-5 sm:h-4 sm:w-4" />
                  <span className="leading-tight sm:hidden">{tab.mobileLabel}</span>
                  <span className="hidden sm:inline">{tab.label}</span>
                  {tab.id === "documents" && pendingAckCount > 0 ? (
                    <span className="absolute right-1 top-1 inline-flex min-w-[1.1rem] items-center justify-center rounded-full bg-amber-500 px-1 text-[10px] font-bold leading-tight text-white sm:static sm:px-1.5">
                      {pendingAckCount}
                    </span>
                  ) : null}
                  {tab.id === "form_templates" && formTemplatesCatalog.length > 0 ? (
                    <span className="absolute right-1 top-1 inline-flex min-w-[1.1rem] items-center justify-center rounded-full bg-indigo-500 px-1 text-[10px] font-bold leading-tight text-white sm:static sm:px-1.5">
                      {formTemplatesCatalog.length}
                    </span>
                  ) : null}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setToolsDialogOpen(true)}
                className="flex min-h-[3rem] flex-col items-center justify-center gap-1 rounded-xl px-2 py-2 text-xs font-medium text-gray-500 transition-all duration-200 hover:bg-white/60 hover:text-gray-900 sm:min-h-0 sm:flex-1 sm:flex-row sm:gap-2 sm:px-3 sm:py-2.5 sm:text-sm"
              >
                <WrenchScrewdriverIcon className="h-5 w-5 sm:h-4 sm:w-4" />
                <span className="leading-tight sm:hidden">{t("tools")}</span>
                <span className="hidden sm:inline">{t("tools")}</span>
              </button>
            </div>
          </div>

          {/* Scrollable content — compact DashboardHeader uses h-screen + overflow-hidden */}
          <div className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-contain">
            <div className="bg-white p-3 pb-8 sm:rounded-2xl sm:border sm:border-gray-200 sm:p-6 sm:shadow-sm">
              {renderContent()}
            </div>
          </div>
        </div>
      </DashboardHeader>

      {/* Colleague Dialog */}
      <ColleagueDialog
        isOpen={colleagueDialogOpen}
        onClose={() => setColleagueDialogOpen(false)}
        colleague={selectedColleague}
      />

      <EmployeeToolsDialog
        open={toolsDialogOpen}
        onClose={() => setToolsDialogOpen(false)}
        userId={currentUser?.id}
      />

      {/* Document View Dialog */}
      <Transition.Root show={documentDialogOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setDocumentDialogOpen(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto overscroll-contain">
            <div className="flex min-h-full items-stretch justify-center p-0 sm:items-center sm:p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative flex min-h-full w-full transform flex-col overflow-hidden bg-white text-left shadow-xl transition-all sm:min-h-0 sm:max-h-[90vh] sm:max-w-4xl sm:rounded-lg">
                  {/* Header */}
                  <div className="flex items-start justify-between gap-3 border-b border-gray-200 p-4 sm:p-6">
                    <div className="flex min-w-0 flex-1 items-start gap-3">
                      <div className="hidden rounded-lg bg-blue-100 p-2 sm:block">
                        <DocumentTextIcon className="h-6 w-6 text-blue-600" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <Dialog.Title className="break-words text-base font-semibold text-gray-900 sm:text-lg">
                          {selectedDocument?.title || t("Dokument")}
                        </Dialog.Title>
                        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                          {selectedDocument?.document_type_display && (
                            <span className="text-xs text-gray-500">
                              {selectedDocument.document_type_display}
                            </span>
                          )}
                          {selectedDocument?.category_display && (
                            <span className="text-xs text-gray-400">
                              • {selectedDocument.category_display}
                            </span>
                          )}
                          {selectedDocument?.chapter && (
                            <span className="text-xs text-gray-400">
                              • {selectedDocument.chapter.title}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => setDocumentDialogOpen(false)}
                      className="ml-1 shrink-0 text-gray-400 transition-colors duration-200 hover:text-gray-600"
                    >
                      <XCircleIcon className="h-6 w-6" />
                    </button>
                  </div>

                  {/* Content */}
                  <div
                    ref={documentContentScrollRef}
                    className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-6"
                  >
                    {renderChapterSiblingDocuments()}
                    {canFillFormFromHandbook && !loadingDocument ? (
                      <div className="mb-4 rounded-lg border border-indigo-200 bg-indigo-50 p-4">
                        <p className="text-sm font-semibold text-indigo-900">
                          {t("documentForms.hse.fillable_template_title")}
                        </p>
                        <p className="mt-1 text-sm text-indigo-800">
                          {t("documentForms.hse.fillable_template_description")}
                        </p>
                      </div>
                    ) : null}
                    {loadingDocument ? (
                      <div className="text-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                        <p className="mt-4 text-gray-500">{t("Laster dokument...")}</p>
                      </div>
                    ) : (
                      (() => {
                        const attachedDocument =
                          documentContent?.attached_document ||
                          selectedDocument?.attached_document ||
                          null;
                        const attachedDocumentId =
                          attachedDocument?.id ||
                          documentContent?.attached_document_id ||
                          selectedDocument?.attached_document_id ||
                          null;
                        const hasFileContent =
                          documentContent?.content_type === "file" ||
                          selectedDocument?.content_type === "file" ||
                          !!attachedDocumentId;

                        if (hasFileContent) {
                          return (
                            <div className="promang-rich-text-content">
                              <DocumentFileContent
                                attachedDocument={attachedDocument}
                                attachedDocumentId={attachedDocumentId}
                              />
                            </div>
                          );
                        }

                        if (documentContent?.content) {
                          return (
                            <div className="prose prose-sm max-w-none overflow-x-auto promang-rich-text-content">
                              <HtmlTextFormatter string={documentContent.content} />
                            </div>
                          );
                        }

                        if (documentContent?.description) {
                          return (
                            <div className="prose prose-sm max-w-none promang-rich-text-content">
                              <p className="text-gray-700">{documentContent.description}</p>
                            </div>
                          );
                        }

                        if (canFillFormFromHandbook) {
                          return (
                            <div className="prose prose-sm max-w-none promang-rich-text-content">
                              {documentContent?.description ? (
                                <p className="text-gray-700">{documentContent.description}</p>
                              ) : (
                                <p className="text-gray-600">{t("documentForms.hse.fillable_template_description")}</p>
                              )}
                            </div>
                          );
                        }

                        return (
                          <div className="text-center py-8">
                            <p className="text-gray-500">{t("Ingen innhold tilgjengelig")}</p>
                          </div>
                        );
                      })()
                    )}
                  </div>

                  {/* Footer */}
                  <div className="shrink-0 border-t border-gray-200 p-4 sm:p-6">
                    {documentContent?.approved_version_id &&
                    !canFillFormFromHandbook &&
                    documentContent?.current_user_read_status ? (
                      <div className="mb-4">
                        <ReadRequirementNotice
                          compact
                          documentId={selectedDocument?.id}
                          status={documentContent.current_user_read_status}
                          onAcknowledge={handleAcknowledgeRead}
                          acknowledging={ackSubmitting}
                          onRequirementChange={(requirement) => {
                            setDocumentContent((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    current_user_read_status: {
                                      ...(prev.current_user_read_status || {}),
                                      requirement,
                                    },
                                  }
                                : prev
                            );
                            loadData();
                          }}
                        />
                      </div>
                    ) : null}
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex flex-wrap items-center gap-2">
                      <DocReadStatusBadge
                        doc={{
                          ...selectedDocument,
                          fillable_template: fillableTemplateForDialog,
                          current_user_read_status:
                            documentContent?.current_user_read_status ??
                            selectedDocument?.current_user_read_status,
                        }}
                      />
                    </div>
                    <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
                      {canFillFormFromHandbook ? (
                        <button
                          type="button"
                          disabled={Boolean(creatingFormTemplateId)}
                          onClick={() =>
                            createFormFromTemplate(
                              {
                                ...selectedDocument,
                                fillable_template: fillableTemplateForDialog,
                              },
                              { closeDocumentDialog: true }
                            )
                          }
                          className="inline-flex w-full items-center justify-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-indigo-700 disabled:opacity-60 sm:w-auto"
                        >
                          {creatingFormTemplateId
                            ? t("Loading")
                            : t("documentForms.hse.fill_form")}
                        </button>
                      ) : null}
                      <Link
                        to={`/dashboard/hse-ks/documents/${selectedDocument?.id}`}
                        className="inline-flex w-full items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 sm:w-auto"
                        onClick={() => setDocumentDialogOpen(false)}
                      >
                        {t("employee_handbook.open_full_document")}
                      </Link>
                      <button
                        type="button"
                        onClick={() => setDocumentDialogOpen(false)}
                        className="w-full rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 sm:w-auto"
                      >
                        {t("Lukk")}
                      </button>
                    </div>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      <NewDocumentFormDialog
        open={showFillFormDialog}
        onClose={() => {
          setShowFillFormDialog(false);
          setFillFormTemplateId(null);
          setFillFormDefaultTitle("");
        }}
        defaultTemplateId={fillFormTemplateId || fillableTemplateForDialog?.id}
        defaultTitle={fillFormDefaultTitle || selectedDocument?.title}
      />

      {/* Org Charts Dialog */}
      {orgChartDialogOpen && (
        <div className="fixed inset-0 z-50 h-full w-full overflow-y-auto bg-gray-600 bg-opacity-50">
          <div className="min-h-full w-full bg-white p-4 shadow-lg sm:relative sm:top-20 sm:mx-auto sm:min-h-0 sm:w-full sm:max-w-lg sm:rounded-md sm:border sm:p-5">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {t("Velg organisasjonskart")}
                </h3>
                <button
                  onClick={() => setOrgChartDialogOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircleIcon className="h-5 w-5" />
                </button>
              </div>
              {orgCharts.length === 0 ? (
                <div className="text-center py-8">
                  <UserGroupIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">{t("Ingen godkjente organisasjonskart tilgjengelig")}</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {orgCharts.map((chart) => (
                    <Link
                      key={chart.id}
                      to={`/dashboard/hse-ks/org-charts/${chart.id}`}
                      onClick={() => setOrgChartDialogOpen(false)}
                      className="block p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <p className="break-words font-medium text-gray-900">{chart.title}</p>
                          {chart.description && (
                            <p className="mt-1 break-words text-sm text-gray-500">{chart.description}</p>
                          )}
                          {chart.approved_revision && (
                            <div className="flex items-center space-x-2 mt-1">
                              <CheckCircleIcon className="h-4 w-4 text-green-500" />
                              <span className="text-xs text-gray-500">
                                {t("Revisjon")} {chart.approved_revision.revision_number}
                              </span>
                            </div>
                          )}
                        </div>
                        <ChevronRightIcon className="h-5 w-5 text-gray-400" />
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
